// useCycleAutofill — 經期事件的「自動計算時間」（W58.0）。
//
// **這是使用者那句「沒有自動計算時間」最直接的落地處。** 建立一個經期
// 事件時，結束日從來都要逐日手選 —— 而「這次大概會持續幾天」這件事，
// 系統其實已經從過往的記錄裡算出來了（平均經期長度）。
//
// 抽成 hook 而不是寫在 `EventForm` 裡，有三個理由：
//
//  1. `EventForm.tsx` 已經 23 KB / 581 行（§3 #10 的 route 組件檔案上限是
//     15 KB），再塞邏輯只會更糟；
//  2. 這段邏輯的價值全在**邊界條件**（什麼時候自動套用、什麼時候不套用），
//     獨立的 hook 比較容易測；
//  3. 它只需要知道「選了哪些天」「標題是什麼」「類型是什麼」，不需要知道
//     表單的其餘部分。
//
// **什麼時候自動套用**（每一條都是刻意的）：
//
//   - 類型切成「週期」時 —— 不是開表單時（開表單時使用者還沒表態）；
//   - 只選了 1 天時 —— 使用者已經手選了一串日期，那就是他的決定；
//   - 有平均經期長度時 —— 沒有推算就不猜（「寧可沒有，不要錯的」）；
//   - 不是編輯既有事件時 —— 那是使用者已經存在的資料。
//
// **標題空著時填「經期」**：標題是必填欄位，讓使用者自己打「經期」兩個字
// 是白給他找事。使用者自己打了字就不動。

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getCycle, type OwnedCategory } from "@/lib/api/calendar";

export interface CycleAutofill {
  /** 平均經期長度（天）。沒有推算值時是 `null` —— 那就不套用。 */
  periodLength: number | null;
  /** 真的展開過選取（用來決定要不要顯示說明那一行）。 */
  expanded: boolean;
  /** 把選取還原成一天。 */
  undo: () => void;
  /** 類型改變時呼叫：換出週期就把狀態清乾淨，換回週期就重新考慮一次。 */
  onCategoryChange: (next: OwnedCategory) => void;
}

export function useCycleAutofill({
  category,
  days,
  title,
  isEdit,
  onDaysChange,
  onTitleChange,
}: {
  category: OwnedCategory;
  days: string[];
  title: string;
  isEdit: boolean;
  onDaysChange: (days: string[]) => void;
  onTitleChange: (title: string) => void;
}): CycleAutofill {
  // 編輯既有事件時不查也不套用 —— 那是使用者已經存在的資料。
  const query = useQuery({
    queryKey: ["calendar-cycle"],
    queryFn: getCycle,
    enabled: !isEdit,
  });
  /**
   * 用多少天來展開。
   *
   * **先看歷史平均值，沒有就看使用者自己的設定（預設 5 天）。**
   * 這兩層的順序很重要：歷史是**這個人的身體**，設定是**這個人的偏好**。
   * 有歷史時用歷史（更準），沒歷史時用設定（至少不會是空白）。
   */
  const periodLength =
    query.data?.projection?.period_length_days ??
    query.data?.settings?.period_length_days ??
    null;

  /** 已經套用過 —— 最多一次，不覆蓋使用者後來的調整。 */
  const appliedRef = useRef(false);
  /** 使用者手動取消過 —— 取消之後不再自動套回來。 */
  const dismissedRef = useRef(false);
  /** 真的展開過選取 —— 這是 UI 那一行的依據。 */
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (isEdit) return;
    if (category !== "cycle") return;
    if (appliedRef.current) return;
    if (dismissedRef.current) return;
    if (!periodLength || periodLength < 2) return;

    appliedRef.current = true;

    if (!title.trim()) {
      onTitleChange("經期");
    }
    // 只在「只選了一天」時展開。使用者已經選了一串日期，那是他的決定。
    if (days.length === 1 && days[0]) {
      const next = expandDays(days[0], periodLength);
      onDaysChange(next);
      setExpanded(true);
    }
  }, [category, periodLength, days, title, isEdit, onDaysChange, onTitleChange]);

  const undo = useCallback(() => {
    dismissedRef.current = true;
    appliedRef.current = false;
    setExpanded(false);
    // 還原成**一天** —— 展開是從 `days[0]` 開始的，所以第一天就是原來那個。
    // 還原成 0 天會讓表單的「建立事件」按鈕變成不可按 —— 那不是「還原」。
    onDaysChange(days.length ? [days[0]!] : []);
  }, [days, onDaysChange]);

  const onCategoryChange = useCallback((next: OwnedCategory) => {
    if (next !== "cycle") {
      appliedRef.current = false;
      dismissedRef.current = false;
      setExpanded(false);
    }
  }, []);

  return {
    periodLength,
    expanded,
    undo,
    onCategoryChange,
  };
}

/** `start` 起的連續 `count` 天（含 start）。 */
function expandDays(start: string, count: number): string[] {
  const out: string[] = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  for (let i = 0; i < count; i += 1) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}
